import React, { Component } from 'react';
import { array, arrayOf, bool, func, object, oneOf, shape, string } from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useHistory, useLocation } from 'react-router-dom';
import debounce from 'lodash/debounce';
import omit from 'lodash/omit';
import classNames from 'classnames';
import { types as sdkTypes } from '../../util/sdkLoader';
import { useIntl, intlShape, FormattedMessage } from '../../util/reactIntl';
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { createResourceLocatorString, pathByRouteName } from '../../util/routes';
import {
  isAnyFilterActive,
  isMainSearchTypeKeywords,
  isOriginInUse,
  getQueryParamNames,
} from '../../util/search';
import { getLanguageFromPath, parse } from '../../util/urlHelpers';
import { propTypes } from '../../util/types';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';

import { H1, H3, H5, ModalInMobile, Page } from '../../components';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';

import { setActiveListing } from './SearchPage.duck';
import {
  groupListingFieldConfigs,
  initialValues,
  searchParamsPicker,
  validUrlQueryParamsFromProps,
  validFilterParams,
  cleanSearchFromConflictingParams,
  createSearchResultSchema,
  pickLocationSearchParams,
} from './SearchPage.shared';

import FilterComponent from './FilterComponent';
import SearchMap from './SearchMap/SearchMap';
import MainPanelHeader from './MainPanelHeader/MainPanelHeader';
import SearchFiltersSecondary from './SearchFiltersSecondary/SearchFiltersSecondary';
import SearchFiltersPrimary from './SearchFiltersPrimary/SearchFiltersPrimary';
import SearchFiltersMobile from './SearchFiltersMobile/SearchFiltersMobile';
import SortBy from './SortBy/SortBy';
import SearchResultsPanel from './SearchResultsPanel/SearchResultsPanel';
import NoSearchResultsMaybe from './NoSearchResultsMaybe/NoSearchResultsMaybe';
import RecommendationsNearby from './RecommendationsNearby/RecommendationsNearby';
import CategoryStrip from './MainPanelHeader/CategoryStrip';
import ImpactBanner from './ImpactBanner/ImpactBanner';
import config from '../../config';
import css from './SearchPage.module.css';
// Search redesign foundation: ships shared design tokens (:root) + fonts. Additive.
import './searchTheme.module.css';
import { filterGuestsAmount, getPaginationData } from './SearchFilter.util';
import { getCountryImage } from './image-bg';
import { getDisplayedListingsCount } from './SearchPage.utils';
import { getTranslatedContinent } from '../../util/translateLocationNames';
import { useTopCountryCategory } from './useTopCountryCategory';

const MODAL_BREAKPOINT = 768; // Search is in modal on mobile layout
const SEARCH_WITH_MAP_DEBOUNCE = 300; // Little bit of debounce before search is initiated.

// Primary filters have their content in dropdown-popup.
// With this offset we move the dropdown to the left a few pixels on desktop layout.
const FILTER_DROPDOWN_OFFSET = -14;

const guestsAmountFilter = {
  key: 'guests',
  schemaType: 'guests',
  label: 'Guests',
  min: 0,
  max: 15,
  step: 2,
};

// Per-category phrase appended to the results-header subtitle, keyed by the
// `pub_category` option value (the CategoryStrip pills plus the remaining
// configListing enum options). Mirrors the redesign prototype's `catPhrase`
// map (socialbnb-search-desktop-mobile.html) so the subtitle reads naturally,
// e.g. "Geprüfte Projekte im Meeresschutz". A key with no entry here simply
// contributes no phrase.
const VERIFIED_PROJECTS_CATEGORY_MESSAGE_IDS = {
  Meeresschutz: 'SearchPageWithMap.verifiedProjectsCatMeeresschutz',
  Tierschutz: 'SearchPageWithMap.verifiedProjectsCatTierschutz',
  Naturschutz: 'SearchPageWithMap.verifiedProjectsCatNaturschutz',
  Kultur: 'SearchPageWithMap.verifiedProjectsCatKultur',
  Abenteuer: 'SearchPageWithMap.verifiedProjectsCatAbenteuer',
  Bildung: 'SearchPageWithMap.verifiedProjectsCatBildung',
  Equality: 'SearchPageWithMap.verifiedProjectsCatEquality',
  Health: 'SearchPageWithMap.verifiedProjectsCatHealth',
  Sports: 'SearchPageWithMap.verifiedProjectsCatSports',
};

// A search `address` value is "empty" if it is missing or one of the string
// sentinels the query params can carry.
const isMeaningfulAddress = address =>
  typeof address === 'string' &&
  address.trim() !== '' &&
  address !== 'undefined' &&
  address !== 'null';

// Builds the results-header subtitle dynamically from the active category and
// location, mirroring the redesign prototype's headline logic:
//   base ("Geprüfte Projekte")
//     + category phrase   (when a category is selected)
//     + " in {location}"  (when a location is set)
//     + " vor Ort"        (only when neither a category nor a location is set)
const buildVerifiedProjectsSubtitle = (validQueryParams, intl) => {
  const rawCategory = validQueryParams?.pub_category;

  const categoryValue =
    typeof rawCategory === 'string'
      ? (rawCategory.startsWith('has_all:') || rawCategory.startsWith('has_any:')
          ? rawCategory.substring(8)
          : rawCategory
        )
          .split(',')
          .filter(Boolean)[0]
      : null;

  const categoryMessageId = categoryValue
    ? VERIFIED_PROJECTS_CATEGORY_MESSAGE_IDS[categoryValue]
    : null;

  const category = categoryMessageId
    ? intl.formatMessage({ id: categoryMessageId })
    : null;

  const address = validQueryParams?.address;
  const location = isMeaningfulAddress(address)
    ? address
    : validQueryParams?.continent
    ? getTranslatedContinent(validQueryParams.continent, intl)
    : null;

  if (category && location) {
    return intl.formatMessage(
      {
        id: 'SearchPageWithMap.verifiedProjectsCategoryLocation',
      },
      { category, location }
    );
  }

  if (category) {
    return intl.formatMessage(
      {
        id: 'SearchPageWithMap.verifiedProjectsCategory',
      },
      { category }
    );
  }

  if (location) {
    return intl.formatMessage(
      {
        id: 'SearchPageWithMap.verifiedProjectsLocation',
      },
      { location }
    );
  }

  return intl.formatMessage({
    id: 'SearchPageWithMap.verifiedProjects',
  });
};

export class SearchPageComponent extends Component {
  generateSEOData(searchParams, intl) {
    const { pub_listingType, address, continent } = searchParams || {};

    let title = intl.formatMessage({ id: 'SearchPageWithMap.defaultTitle' });
    let description = intl.formatMessage({ id: 'SearchPageWithMap.defaultDescription' });
    let h1 = intl.formatMessage({ id: 'SearchPageWithMap.defaultH1' });

    if (pub_listingType === 'impact-accommodation') {
      if (address) {
        title = intl.formatMessage({ id: 'SearchPageWithMap.accDestTitle' }, { address });
        description = intl.formatMessage(
          { id: 'SearchPageWithMap.accDestDescription' },
          { address }
        );
        h1 = intl.formatMessage({ id: 'SearchPageWithMap.accDestH1' }, { address });
      } else {
        title = intl.formatMessage({ id: 'SearchPageWithMap.accTitle' });
        description = intl.formatMessage({ id: 'SearchPageWithMap.accDescription' });
        h1 = intl.formatMessage({ id: 'SearchPageWithMap.accH1' });
      }
    } else if (pub_listingType === 'impact-experience') {
      if (address) {
        title = intl.formatMessage({ id: 'SearchPageWithMap.expDestTitle' }, { address });
        description = intl.formatMessage({ id: 'SearchPageWithMap.expDestDescription' });
        h1 = intl.formatMessage({ id: 'SearchPageWithMap.expDestH1' }, { address });
      } else {
        title = intl.formatMessage({ id: 'SearchPageWithMap.expTitle' });
        description = intl.formatMessage({ id: 'SearchPageWithMap.expDescription' });
        h1 = intl.formatMessage({ id: 'SearchPageWithMap.expH1' });
      }
    } else if (!pub_listingType && address) {
      title = intl.formatMessage({ id: 'SearchPageWithMap.destTitle' }, { address });
      description = intl.formatMessage({ id: 'SearchPageWithMap.destDescription' }, { address });
      h1 = intl.formatMessage({ id: 'SearchPageWithMap.destH1' }, { address });
    } else if (!pub_listingType && continent) {
      const translatedContinent = getTranslatedContinent(continent, intl);
      title = intl.formatMessage(
        { id: 'SearchPageWithMap.destTitleContinent' },
        { continent: translatedContinent }
      );
      description = intl.formatMessage(
        { id: 'SearchPageWithMap.destDescriptionContinent' },
        { continent: translatedContinent }
      );
      h1 = intl.formatMessage(
        { id: 'SearchPageWithMap.destH1Continent' },
        { continent: translatedContinent }
      );
    }

    return { title, description, h1 };
  }

  constructor(props) {
    if (typeof window !== 'undefined' && window?.document) {
      document?.body?.scrollTo({
        top: 0,
      });
      // check if firefox handle scroll to the top
      const isFirefox = navigator?.userAgent?.toLowerCase().indexOf('firefox') > -1;
      if (isFirefox) {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }
    }
    super(props);

    const { location } = this.props;
    const queryParams = parse(location.search);
    const pubListingType = queryParams?.pub_listingType || null;
    const searchCountry = queryParams?.country || null;
    const searchAddress = queryParams?.address || null;
    const searchContinent = queryParams?.continent || null;

    this.state = {
      isSearchMapOpenOnMobile: props.tab === 'map',
      isMobileModalOpen: false,
      currentQueryParams: validUrlQueryParamsFromProps(props),
      isSecondaryFiltersOpen: false,
      filterNewParms: {},
      sortByWalker: {},
      userEmail: 'false',
      resetRouter: false,
      listingTypeSearch: pubListingType,
      mapState: {},
      searchFilterIsPlain: false,
      currentBounds: {},
      isMobileLayout: false,
      country: searchCountry,
      continent: searchContinent,
      previousAddress: searchAddress,
      searchButtonHovered: false,
    };

    this.onMapMoveEnd = debounce(this.onMapMoveEnd.bind(this), SEARCH_WITH_MAP_DEBOUNCE);
    this.onOpenMobileModal = this.onOpenMobileModal.bind(this);
    this.onCloseMobileModal = this.onCloseMobileModal.bind(this);

    this.applyFilters = this.applyFilters.bind(this);
    this.cancelFilters = this.cancelFilters.bind(this);
    this.resetAll = this.resetAll.bind(this);
    this.getHandleChangedValueFn = this.getHandleChangedValueFn.bind(this);

    this.handleSortBy = this.handleSortBy.bind(this);
    this.handleLoadMore = this.handleLoadMore.bind(this);

    this.zoomOutBoundsIfNeeded = this.zoomOutBoundsIfNeeded.bind(this);
    this.isCountryOnly = this.isCountryOnly.bind(this);
    this.isContinentOnly = this.isContinentOnly.bind(this);
    this.trySetUserEmail = this.trySetUserEmail.bind(this);
    this.setListingTypeFilter = this.setListingTypeFilter.bind(this);
    this.hasActiveFilters = this.hasActiveFilters.bind(this);
    this.shouldForceShowFilters = this.shouldForceShowFilters.bind(this);
  }

  getLanguage() {
    const { location } = this.props;
    return getLanguageFromPath(location.pathname);
  }

  // Function to check if bounds have changed and update URL accordingly
  updateUrlIfBoundsChanged(newBounds, isUserInitiated = false) {
    // Extract the current bounds from the URL
    const urlParams = new URLSearchParams(this.props.location.search);
    const currentBounds = urlParams.get('bounds');

    // Check if the bounds have changed
    if (currentBounds !== newBounds) {
      // Only drop location context when the user manually moved the map
      if (isUserInitiated) {
        urlParams.delete('country');
        urlParams.delete('address');
        urlParams.delete('continent');
        urlParams.set('mapSearch', 'true');
      }

      // Update the URL with the new bounds
      urlParams.set('bounds', newBounds);

      // Use React Router history to update URL without page reload
      const { history, routeConfiguration } = this.props;
      const lang = this.getLanguage();
      const searchParams = Object.fromEntries(urlParams.entries());

      history.replace(
        createResourceLocatorString('SearchPage', routeConfiguration, { lang }, searchParams)
      );

      return true; // Indicate that bounds were updated
    }

    return false; // Indicate that bounds were not updated
  }

  onMapMoveEnd(viewportBoundsChanged, data, takeViewBounds = false) {
    const { viewportBounds, viewportCenter, isUserInitiated = false } = data || {};

    // Only a genuine user gesture (pan / zoom / drag) may rewrite the URL bounds
    // and re-run the search. Programmatic camera moves — the initial fit, fitting
    // to a destination picked elsewhere, or a re-fit triggered when the map's
    // container is resized as the page settles — must NOT write back to the URL.
    // Doing so retriggers componentDidUpdate → fitMapToBounds → moveend in an
    // endless loop: the map visibly drifts from one zone to another and every
    // reader of the search string (e.g. the category-count badges) thrashes
    // between 0 and the real total. Mapbox reports `originalEvent` only for
    // user-driven moves, which is what `isUserInitiated` reflects. The initial
    // search is dispatched from the URL by loadData, so gating here doesn't drop
    // it.
    if (!isUserInitiated) {
      return;
    }

    this.setState({ mapState: { viewportBoundsChanged, data } });
    const routes = this.props.routeConfiguration;
    const lang = this.getLanguage();

    const searchPagePath = pathByRouteName('SearchPage', routes, {
      lang,
    });
    const currentPath = this.props.location.pathname;

    const isSearchPage = currentPath.startsWith(searchPagePath);

    if (viewportBoundsChanged && isSearchPage) {
      const { history, location, config } = this.props;
      const { listingFields: listingFieldsConfig } = config?.listing || {};
      const { defaultFilters: defaultFiltersConfig } = config?.search || {};

      const {
        address,
        bounds,
        origin,
        mapSearch,
        country,
        continent,
        pub_listingType,
        ...rest
      } = parse(location.search, {
        latlng: ['origin'],
        latlngBounds: ['bounds'],
      });

      const isNewAddress = address !== this.state.previousAddress;

      const originMaybe = isOriginInUse(this.props.config) ? { origin: viewportCenter } : {};
      let finalbounds;
      if (this.state.isMobileLayout) {
        finalbounds = takeViewBounds
          ? Object.keys(this.state.currentBounds).length > 0
            ? this.state.currentBounds
            : viewportBounds
          : viewportBounds
          ? viewportBounds
          : this.state.currentBounds;
        this.setState({ currentBounds: finalbounds });
      } else {
        finalbounds =
          Object.keys(viewportBounds).length > 0 ? viewportBounds : this.state.currentBounds;
      }

      // Call the new function to update URL if bounds changed
      if (finalbounds && Object.keys(finalbounds).length > 0) {
        const boundsString = `${finalbounds.ne.lat},${finalbounds.ne.lng},${finalbounds.sw.lat},${finalbounds.sw.lng}`;
        const boundsChanged = this.updateUrlIfBoundsChanged(boundsString, isUserInitiated);

        // If bounds were updated, don't continue with the normal flow
        if (boundsChanged) {
          return;
        }
      }

      const searchParams = {
        address,
        origin,
        country,
        continent,
        bounds: finalbounds,
        mapSearch: true,
        pub_listingType: this.state.listingTypeSearch,
        ...validFilterParams(rest, listingFieldsConfig, [
          ...defaultFiltersConfig,
          guestsAmountFilter,
        ]),
      };

      this.setState({ previousAddress: address });

      history.push(createResourceLocatorString('SearchPage', routes, { lang }, searchParams));
    } else {
      const { history, location, config } = this.props;
      const { listingFields: listingFieldsConfig } = config?.listing || {};
      const { defaultFilters: defaultFiltersConfig } = config?.search || {};
      const {
        address,
        bounds,
        mapSearch,
        origin,
        country,
        continent,
        pub_listingType,
        ...rest
      } = parse(location.search, {
        latlng: ['origin'],
        latlngBounds: ['bounds'],
      });

      const searchParams = {
        address,
        bounds:
          viewportBounds && Object.keys(viewportBounds).length > 0
            ? viewportBounds
            : this.state.currentBounds,
        country,
        continent,
        origin,
        mapSearch: true,
        pub_listingType: this.state.listingTypeSearch,
        ...validFilterParams(rest, listingFieldsConfig, [
          ...defaultFiltersConfig,
          guestsAmountFilter,
        ]),
      };

      this.setState({ previousAddress: address });

      history.replace(createResourceLocatorString('SearchPage', routes, { lang }, searchParams));
    }
  }

  setListingTypeFilter(listingType) {
    const { history, location, routeConfiguration, config } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
    const lang = this.getLanguage();

    this.setState({ listingTypeSearch: listingType }, () => {
      const searchFromURL = parse(location.search, {
        latlng: ['origin'],
        latlngBounds: ['bounds'],
      });
      const { page, ...searchWithoutPage } = searchFromURL;
      const locationParams = pickLocationSearchParams(searchFromURL);
      const filterParams = validFilterParams(
        searchWithoutPage,
        listingFieldsConfig,
        [...defaultFiltersConfig, guestsAmountFilter]
      );

      const searchParams = cleanSearchFromConflictingParams(
        {
          ...locationParams,
          ...filterParams,
          ...(searchFromURL.mapSearch != null ? { mapSearch: searchFromURL.mapSearch } : {}),
          pub_listingType: listingType,
        },
        listingFieldsConfig,
        defaultFiltersConfig,
        sortConfig
      );

      history.push(
        createResourceLocatorString('SearchPage', routeConfiguration, { lang }, searchParams)
      );

      if (typeof window === 'undefined' || !window.document) {
        return;
      }

      const scrollSearchPageToTop = () => {
        window.scrollTo(0, 0);
        const docEl = document.documentElement;
        if (docEl) {
          docEl.scrollTop = 0;
          docEl.scrollLeft = 0;
        }
        document.body.scrollTop = 0;
        document.body.scrollLeft = 0;
        document.body.scrollTo({ top: 0, left: 0 });
      };

      scrollSearchPageToTop();
      window.requestAnimationFrame(scrollSearchPageToTop);
      window.setTimeout(scrollSearchPageToTop, 0);
    });
  }

  onOpenMobileModal() {
    this.setState({ isMobileModalOpen: true });
  }

  onCloseMobileModal() {
    this.setState({ isMobileModalOpen: false });
  }

  applyFilters() {
    const { history, routeConfiguration, config } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    const searchParams = { ...urlQueryParams, ...this.state.currentQueryParams };
    const search = cleanSearchFromConflictingParams(
      searchParams,
      listingFieldsConfig,
      defaultFiltersConfig,
      sortConfig
    );

    const lang = this.getLanguage();

    history.push(createResourceLocatorString('SearchPage', routeConfiguration, { lang }, search));
  }

  cancelFilters() {
    this.setState({ currentQueryParams: {} });
  }

  resetAll(e) {
    const { history, routeConfiguration, config } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig } = config?.search || {};

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    const filterQueryParamNames = getQueryParamNames(listingFieldsConfig, defaultFiltersConfig);

    this.setState({ currentQueryParams: {} });

    filterQueryParamNames.push('guests');
    filterQueryParamNames.push('pub_listingType');
    const queryParams = omit(urlQueryParams, filterQueryParamNames);

    const lang = this.getLanguage();

    history.push(
      createResourceLocatorString('SearchPage', routeConfiguration, { lang }, queryParams)
    );
  }

  getHandleChangedValueFn(useHistoryPush) {
    const { history, routeConfiguration, config } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    if (urlQueryParams.guests == '0,15') {
      urlQueryParams.guests = null;
    }

    urlQueryParams.pub_listingType = this.state.listingTypeSearch;
    return updatedURLParams => {
      const locationParams = pickLocationSearchParams(
        parse(this.props.location.search, {
          latlng: ['origin'],
          latlngBounds: ['bounds'],
        })
      );

      const updater = prevState => {
        const { keywords } = urlQueryParams;
        const mergedQueryParams = { ...urlQueryParams, ...prevState.currentQueryParams };

        const keywordsMaybe = isMainSearchTypeKeywords(config) ? { keywords } : {};
        Object.values(updatedURLParams)[0] == null
          ? this.setState({ filterNewParms: {} })
          : this.setState({ filterNewParms: updatedURLParams });
        return {
          currentQueryParams: {
            ...mergedQueryParams,
            ...updatedURLParams,
            ...keywordsMaybe,
            ...locationParams,
            pub_listingType: this.state.listingTypeSearch,
          },
        };
      };

      const callback = () => {
        const lang = this.getLanguage();

        const searchParams = this.state.currentQueryParams;
        const search = cleanSearchFromConflictingParams(
          searchParams,
          listingFieldsConfig,
          defaultFiltersConfig,
          sortConfig
        );

        history.push(
          createResourceLocatorString('SearchPage', routeConfiguration, { lang }, search)
        );
      };

      this.setState(updater, callback);
    };
  }

  handleSortBy(urlParam, values) {
    const { history, routeConfiguration, location } = this.props;

    const currentParams = parse(location.search, {
      latlng: ['origin'],
      latlngBounds: ['bounds'],
    });

    const queryParams = values
      ? { ...currentParams, [urlParam]: values }
      : omit(currentParams, urlParam);

    const sortByStr = values == 'price' ? 'HigherPriceSort' : 'LowerPriceSort';
    const sortElbWalkerAction = { ['data-elbaction']: `click:sortByClick-${sortByStr}` };
    this.setState({ sortByWalker: sortElbWalkerAction });

    const lang = this.getLanguage();

    history.push(
      createResourceLocatorString('SearchPage', routeConfiguration, { lang }, queryParams)
    );
  }

  // Mobile "Load more": advance the page in the URL. The results panel accumulates
  // pages client-side, so this appends rather than replaces. `loadMoreInProgress`
  // suppresses the page-change scroll-to-top so the view stays put.
  handleLoadMore(nextPage) {
    const { history, routeConfiguration, location } = this.props;

    const currentParams = parse(location.search, {
      latlng: ['origin'],
      latlngBounds: ['bounds'],
    });

    const queryParams = { ...currentParams, page: nextPage };
    const lang = this.getLanguage();

    this.loadMoreInProgress = true;
    history.push(
      createResourceLocatorString('SearchPage', routeConfiguration, { lang }, queryParams)
    );
  }

  isCountryOnly(queryString) {
    if (queryString.startsWith('?')) {
      queryString = queryString.substring(1);
    }
    const params = new URLSearchParams(queryString);
    let address = params.get('address');
    if (address) {
      address = decodeURIComponent(address);
    } else {
      return true;
    }
    const parts = address && address?.split(',').map(part => part?.trim());

    if (parts !== null) {
      return parts.length === 1;
    } else {
      return;
    }
  }

  isContinentOnly(queryString) {
    if (queryString.startsWith('?')) {
      queryString = queryString.substring(1);
    }
    const params = new URLSearchParams(queryString);
    let address = params.get('address');
    if (address) {
      address = decodeURIComponent(address);
    } else {
      return true;
    }
    const parts = address && address?.split(',').map(part => part?.trim());

    if (parts !== null) {
      return parts.length === 1;
    } else {
      return;
    }
  }

  zoomOutBoundsIfNeeded(queryString) {
    return queryString;

    if (!this.isCountryOnly(queryString)) {
      return queryString;
    }
    const params = new URLSearchParams(
      queryString.startsWith('?') ? queryString.substr(1) : queryString
    );
    const bounds = params
      .get('bounds')
      .split(',')
      .map(Number);

    const lat1 = bounds[0];
    const lon1 = bounds[1];
    const lat2 = bounds[2];
    const lon2 = bounds[3];
    const latDiff = (lat1 - lat2) * 2; // -200% of the height
    const lonDiff = (lon1 - lon2) * 2; // -200% of the width
    const newBounds = [lat1 + latDiff, lon1 + lonDiff, lat2 - latDiff, lon2 - lonDiff].join(',');
    params.set('bounds', newBounds);

    return params.toString();
  }
  trySetUserEmail(email) {
    if (this.state.userEmail == 'false' && email != this.state.userEmail) {
      this.setState({ userEmail: email });
    }
  }

  hasActiveFilters(validQueryParams) {
    const { address } = validQueryParams;

    return !!(address && address !== 'undefined' && address !== 'null' && address.trim() !== '');
  }

  shouldForceShowFilters(validQueryParams) {
    const hasFiltersWithValues = this.hasActiveFilters(validQueryParams);

    return hasFiltersWithValues;
  }

  handleSearchButtonHover = isHovered => {
    this.setState({ searchButtonHovered: isHovered });
  };

  clearFocusAndCloseFilters = () => {
    this.setState({ searchFilterIsPlain: false });

    // Check if the click target is within the language switcher
    const languageSwitcher = document.querySelector(
      '[data-elbaction="click:click topbar-languageswitch"]'
    );
    if (languageSwitcher && languageSwitcher.contains(document.activeElement)) {
      return; // Don't clear focus if clicking on language switcher
    }

    setTimeout(() => {
      if (document.activeElement && document.activeElement.blur) {
        document.activeElement.blur();
      }
    }, 0);
    if (document.activeElement && document.activeElement.blur) {
      document.activeElement.blur();
    }

    const searchInputs = document.querySelectorAll(
      'input[type="text"], input[type="search"], input'
    );
    searchInputs.forEach(input => {
      if (input && input.blur) {
        input.blur();
      }
    });
    setTimeout(() => {
      if (document.body && document.body.focus) {
        document.body.focus();
      }
    }, 10);
  };

  componentDidMount(prevProps) {
    const { searchParams } = this.props;
    if (searchParams?.bounds) {
      this.setState({ currentBounds: searchParams?.bounds });
    }

    if (typeof window !== 'undefined') {
      if (typeof window !== 'undefined') {
        if (window.innerWidth < 650) {
          this.setState({ isMobileLayout: true });
        } else {
          this.setState({ isMobileLayout: false });
        }
      }
    }

    this.handleGlobalClick = event => {
      if (this.state.searchFilterIsPlain) {
        const searchContainer = document.querySelector('[data-testid="search-container"]');
        const filtersContainer = document.querySelector('[data-testid="filters-container"]');
        const languageSwitcher = document.querySelector(
          '[data-elbaction="click:click topbar-languageswitch"]'
        );

        const isOutsideSearch = searchContainer && !searchContainer.contains(event.target);
        const isOutsideFilters = filtersContainer && !filtersContainer.contains(event.target);
        const isOutsideLanguageSwitcher =
          languageSwitcher && !languageSwitcher.contains(event.target);

        if (isOutsideSearch && isOutsideFilters && isOutsideLanguageSwitcher) {
          this.clearFocusAndCloseFilters();
        }
      }
    };

    document.addEventListener('click', this.handleGlobalClick, true);
    document.addEventListener('mousedown', this.handleGlobalClick, true);
  }

  componentDidUpdate(prevProps) {
    // Check if page parameter changed (pagination click)
    const prevSearch = prevProps.location?.search || '';
    const currentSearch = this.props.location?.search || '';

    const prevParams = new URLSearchParams(prevSearch);
    const currentParams = new URLSearchParams(currentSearch);

    const prevPage = prevParams.get('page');
    const currentPage = currentParams.get('page');
    const pubListingType = currentParams.get('pub_listingType') || null;

    if (pubListingType !== this.state.listingTypeSearch) {
      this.setState({ listingTypeSearch: pubListingType });
    }

    // If page changed, scroll to heroNavigation — except for a mobile "Load more",
    // which appends below and should keep the current scroll position.
    if (prevPage !== currentPage && currentPage !== null) {
      if (this.loadMoreInProgress) {
        this.loadMoreInProgress = false;
      } else {
        this.scrollToHeroNavigation();
      }
    }
  }

  // Pin the sticky category/filter/sort nav to the top of the viewport. Used after
  // a page change and when the Filters panel opens, so the nav (category strip +
  // controls) sits at the top and the dropdown gets the full height below it.
  scrollToHeroNavigation() {
    if (typeof window === 'undefined') {
      return;
    }
    // rAF + timeout so the scroll runs after the current render/layout settles.
    requestAnimationFrame(() => {
      setTimeout(() => {
        const heroNavElement = document.getElementById('heroNavigation');
        if (!heroNavElement) {
          return;
        }
        // Absolute document position of the sticky nav, traversing offsetParents.
        let targetTop = -25;
        let element = heroNavElement;
        while (element) {
          targetTop += element.offsetTop || -25;
          element = element.offsetParent;
        }

        // Force scroll using multiple methods
        window.scroll({ top: targetTop, left: 0, behavior: 'instant' });
        document.documentElement.scrollTop = targetTop;
        document.body.scrollTop = targetTop;
      }, 50);
    });
  }

  componentWillUnmount() {
    if (this.handleGlobalClick) {
      document.removeEventListener('click', this.handleGlobalClick, true);
      document.removeEventListener('mousedown', this.handleGlobalClick, true);
    }
  }

  render() {
    const {
      intl,
      listings,
      location,
      onManageDisableScrolling,
      pagination,
      scrollingDisabled,
      searchInProgress,
      searchListingsError,
      searchParams,
      activeListingId,
      onActivateListing,
      routeConfiguration,
      config,
      history,
      nearbyLabelIndex,
      topCategory,
    } = this.props;

    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};

    const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
    const marketplaceCurrency = config.currency;

    const { title, description, h1 } = this.generateSEOData(searchParams, intl);

    const locationZoomedOut =
      location.search !== '' ? this.zoomOutBoundsIfNeeded(location.search) : '';
    const { searchParamsAreInSync, urlQueryParams, searchParamsInURL } = searchParamsPicker(
      locationZoomedOut,
      searchParams,
      listingFieldsConfig,
      defaultFiltersConfig,
      sortConfig,
      isOriginInUse(config)
    );

    const validQueryParams = validFilterParams(
      searchParamsInURL,
      listingFieldsConfig,
      [...defaultFiltersConfig, guestsAmountFilter],
      false
    );

    const isWindowDefined = typeof window !== 'undefined';
    const isMobileLayout = isWindowDefined && window.innerWidth < MODAL_BREAKPOINT;
    const shouldShowSearchMap =
      !isMobileLayout || (isMobileLayout && this.state.isSearchMapOpenOnMobile);

    const isKeywordSearch = isMainSearchTypeKeywords(config);
    const defaultFilters = isKeywordSearch
      ? defaultFiltersConfig.filter(f => f.key !== 'keywords')
      : defaultFiltersConfig;
    const [customPrimaryFilters, customSecondaryFilters] = groupListingFieldConfigs(
      listingFieldsConfig,
      activeListingTypes
    );

    const mainfilters = ['price', 'roomtype', 'amenities', 'food', 'category'];

    const availablePrimaryFilters = [
      ...defaultFilters,
      ...customPrimaryFilters,
      /*       guestsAmountFilter, */
      ...customSecondaryFilters,
    ].filter(f => mainfilters.includes(f.key));

    const availableFilters = [
      ...customPrimaryFilters,
      guestsAmountFilter,
      ...defaultFilters,
      ...customSecondaryFilters,
    ];

    const hasSecondaryFilters = !!(customSecondaryFilters && customSecondaryFilters.length > 0);

    // Selected aka active filters
    const selectedFilters = validFilterParams(validQueryParams, listingFieldsConfig, [
      ...defaultFiltersConfig,
      guestsAmountFilter,
    ]);
    const keysOfSelectedFilters = Object.keys(selectedFilters);
    const selectedFiltersCountForMobile = isKeywordSearch
      ? keysOfSelectedFilters.filter(f => f !== 'keywords').length
      : keysOfSelectedFilters.length;
    const isValidDatesFilter =
      searchParamsInURL.dates == null ||
      (searchParamsInURL.dates != null && searchParamsInURL.dates === selectedFilters.dates);

    const selectedSecondaryFilters = hasSecondaryFilters
      ? validFilterParams(validQueryParams, [...customSecondaryFilters, guestsAmountFilter], [])
      : {};
    const selectedSecondaryFiltersCount = Object.keys(selectedSecondaryFilters).length;

    const isSecondaryFiltersOpen = !!hasSecondaryFilters && this.state.isSecondaryFiltersOpen;
    const propsForSecondaryFiltersToggle = hasSecondaryFilters
      ? {
          isSecondaryFiltersOpen: this.state.isSecondaryFiltersOpen,
          toggleSecondaryFiltersOpen: isOpen => {
            this.setState({ isSecondaryFiltersOpen: isOpen, currentQueryParams: {} });
            // When opening, pin the nav to the top so the filter panel drops from
            // the top of the viewport instead of wherever the page was scrolled.
            if (isOpen) {
              this.scrollToHeroNavigation();
            }
          },
          selectedSecondaryFiltersCount,
          history,
          urlQueryParams,
        }
      : {};

    const hasPaginationInfo = !!pagination && pagination.totalItems != null;

    // PAGINATION FLOW:
    // 1. API call: sdk.listings.query() in SearchPage.duck.js returns response.data.meta
    // 2. Redux: SEARCH_LISTINGS_SUCCESS action sets pagination = payload.data.meta
    // 3. Component: mapStateToProps gets pagination from state.SearchPage.pagination
    // 4. Note: When nearby listings are added, pagination.meta is NOT updated,
    //    so totalItems may not reflect the actual displayed count

    // Calculate displayed count using the same logic as SearchResultsPanel
    const isDevEnv =
      typeof window !== 'undefined' && window?.location?.hostname?.includes('localhost');

    const displayedCount = getDisplayedListingsCount(listings, nearbyLabelIndex, isDevEnv);

    // Calculate totalItems - use displayed count when we can calculate it accurately
    let totalItems = 0;
    if (searchParamsAreInSync && hasPaginationInfo) {
      // If single page, use the exact displayed count
      if (pagination.totalPages === 1) {
        totalItems = displayedCount;
      } else {
        // For multiple pages, use pagination.totalItems from API
        // (The API knows the total across all pages)
        totalItems = pagination.totalItems;
      }
    } else if (pagination?.paginationUnsupported) {
      // No pagination - use actual displayed count
      totalItems = displayedCount;
    } else if (!searchInProgress && hasPaginationInfo) {
      // URL and store can briefly diverge (e.g. mapSearch flag); still show API count
      totalItems =
        pagination.totalPages === 1 ? displayedCount : pagination.totalItems;
    }

    const listingsAreLoaded =
      !searchInProgress &&
      searchParamsAreInSync &&
      !!(hasPaginationInfo || pagination?.paginationUnsupported);
    const listingsAreLoadedSucc =
      !searchInProgress && !!(hasPaginationInfo || pagination?.paginationUnsupported);
    const conflictingFilterActive = isAnyFilterActive(
      sortConfig.conflictingFilters,
      validQueryParams,
      listingFieldsConfig,
      defaultFiltersConfig
    );
    const sortBy = mode => {
      return sortConfig.active ? (
        <div {...this.state.sortByWalker}>
          <SortBy
            sort={validQueryParams[sortConfig.queryParamName]}
            isConflictingFilterActive={!!conflictingFilterActive}
            hasConflictingFilters={!!(sortConfig.conflictingFilters?.length > 0)}
            selectedFilters={selectedFilters}
            onSelect={this.handleSortBy}
            showAsPopup
            mode={mode}
            contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
          />
        </div>
      ) : null;
    };
    const noResultsInfo = !searchInProgress ? (
      <NoSearchResultsMaybe
        listingsAreLoaded={listingsAreLoaded}
        totalItems={totalItems}
        location={location}
        resetAll={this.resetAll}
      />
    ) : null;

    const { bounds, origin } = searchParamsInURL || {};
    const { schema } = createSearchResultSchema(
      listings,
      searchParamsInURL || {},
      intl,
      routeConfiguration,
      config,
      location
    );

    const topbarClasses = this.state.isMobileModalOpen
      ? classNames(css.topbarBehindModal, css.topbar)
      : css.topbar;

    var elbsearchLocationData =
      validQueryParams?.address !== undefined &&
      validQueryParams?.address !== 'undefined' &&
      validQueryParams?.address !== 'null'
        ? {
            ['data-elb']: `searchp`,
            [`data-elb-searchp`]: `search-search_id:${validQueryParams?.address}`,
            ['data-elbaction']: `visible:vis destp-sinput-${validQueryParams.address}`,
          }
        : {};
    var elbDataFilter =
      Object.keys(this.state.filterNewParms).length != 0
        ? {
            ['data-elbaction']: `click:click destp-filter-${
              Object.values(this.state.filterNewParms)[0]
            }`,
          }
        : {};
    const setUserData = data => {
      if (data) {
        this.trySetUserEmail(data);
      }
    };

    const guestFilter = (
      <FilterComponent
        key={`SearchFiltersPrimary.${guestsAmountFilter.key}`}
        idPrefix="SearchFiltersPrimary"
        config={guestsAmountFilter}
        marketplaceCurrency={marketplaceCurrency}
        urlQueryParams={validQueryParams}
        initialValues={initialValues(this.props, this.state.currentQueryParams)}
        getHandleChangedValueFn={this.getHandleChangedValueFn}
        intl={intl}
        showAsPopup={false}
        contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
      />
    );

    // Prototype layout: the destination search lives in the topbar, not the hero.
    // We render the exact same SearchFiltersPrimary instance (all handlers/state
    // preserved) and inject it into the Navbar via TopbarContainer's searchBarNav.
    const searchBarNav = (
      <SearchFiltersPrimary
        {...propsForSecondaryFiltersToggle}
        history={history}
        urlQueryParams={validQueryParams}
        clearFilter={this.resetAll}
        totalItems={totalItems}
        handleFlatFilter={() => this.setState({ searchFilterIsPlain: true })}
        reduceFilter={() => this.setState({ searchFilterIsPlain: false })}
        currentFilterState={this.state.searchFilterIsPlain}
        guestsFilter={guestFilter}
        validQueryParams={validQueryParams}
        onMapIconClick={() => this.setState({ isSearchMapOpenOnMobile: true })}
        isSearchMapOpenOnMobile={this.state.isSearchMapOpenOnMobile}
        setCurrentBounds={bounds => this.setState({ currentBounds: bounds })}
        searchFilterIsPlain={this.state.searchFilterIsPlain}
        onSearchButtonHover={this.handleSearchButtonHover}
      >
        {availablePrimaryFilters.map(config => {
          return (
            <FilterComponent
              key={`SearchFiltersPrimary.${config.key}`}
              idPrefix="SearchFiltersPrimary"
              config={config}
              marketplaceCurrency={marketplaceCurrency}
              urlQueryParams={validQueryParams}
              initialValues={initialValues(this.props, this.state.currentQueryParams)}
              getHandleChangedValueFn={this.getHandleChangedValueFn}
              intl={intl}
              showAsPopup={false}
              contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
            />
          );
        })}
      </SearchFiltersPrimary>
    );
    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        description={description}
        title={title}
        schema={schema}
        noChildrenMargin={true}
      >
        {/* Hero, sticky listing-type filters, and results share one scroll parent so filters stay sticky */}
        <div className={css.searchPageStickyRoot}>
          <div className={css.heroBanner}>
            <div
              className={classNames(css.heroBackground, {
                [css.heroBackgroundDefault]:
                  !validQueryParams?.country &&
                  !searchParamsInURL?.country &&
                  !validQueryParams?.continent &&
                  !searchParamsInURL?.continent,
              })}
              style={{
                backgroundImage: `url(${getCountryImage(
                  validQueryParams?.country ||
                    searchParamsInURL?.country ||
                    validQueryParams?.continent ||
                    searchParamsInURL?.continent
                )})`,
              }}
            >
              <div className={css.heroOverlay}></div>
            </div>
            <div
              className={classNames(css.heroContent, {
                [css.heroContentMapOpen]: this.state.isSearchMapOpenOnMobile,
              })}
            >
              {/* Trust pills top-left (mockup): static brand trust stats —
                  partners / travelers / rating — matching the search-page design. */}
              <div
                className={classNames(css.heroTrust, {
                  [css.heroTextHidden]: this.state.isSearchMapOpenOnMobile,
                })}
              >
                {/* Pill 1: context-aware. With no country selected it's the
                    static brand figure ("453 Partner"). Once a country is
                    selected it shows that country's most-mentioned category
                    (e.g. "Nature conservation"); falls back to the static figure
                    while the counts load. Pills 2 & 3 stay static. */}
                {topCategory ? (
                  <span className={css.heroTrustPill}>
                    <FormattedMessage id={topCategory.labelId} />
                  </span>
                ) : (
                  <span className={css.heroTrustPill}>
                    <b>453</b>&nbsp;
                    <FormattedMessage
                      id="SearchPage.trustBannerPartners"
                      defaultMessage="Partner"
                    />
                  </span>
                )}
                {/* Pills 2 & 3: static brand stats. */}
                <span className={css.heroTrustPill}>
                  <b>5.000+</b>&nbsp;
                  <FormattedMessage id="SearchPage.trustBannerTravelers" defaultMessage="Reisende" />
                </span>
                <span className={css.heroTrustPill}>
                  <span className={css.heroTrustStar} aria-hidden="true">
                    ★
                  </span>
                  <FormattedMessage
                    id="SearchPageWithMap.heroTrustRating"
                    defaultMessage="4,9 Bewertung"
                  />
                </span>
              </div>
              <div
                className={classNames(css.heroText, {
                  [css.heroTextHidden]: this.state.isSearchMapOpenOnMobile,
                })}
              >
                <span className={css.heroEyebrow}>
                  <FormattedMessage id="SearchPageWithMap.heroEyebrow" />
                </span>
                <h1 className={css.heroTitle}>{h1}</h1>
              </div>
            </div>
          </div>

          {!this.state.isMobileModalOpen &&
          !(this.state.isSearchMapOpenOnMobile && isMobileLayout) ? (
            <TopbarContainer
              className={topbarClasses}
              currentPage="SearchPage"
              currentSearchParams={urlQueryParams}
              setUserEmail={setUserData}
              searchBarNav={searchBarNav}
            />
          ) : null}

          {/* Sticky filter bar: category chips row + controls row (listing-type
              toggle · Filter · Sort). Full content width, matches the desktop
              mockup (new-search-ui/search-page-desktop.html → sticky bar). */}
          <div
            id="heroNavigation"
            className={classNames(css.stickyFilterBar, {
              [css.stickyListingTypeFiltersHiddenOnMobileMap]: this.state.isSearchMapOpenOnMobile,
            })}
          >
            <div className={css.stickyFilterBarInner}>
              <div className={css.categoryRow}>
                <CategoryStrip totalResults={totalItems} />
              </div>

              <div className={css.navigationTabsMobile}>
                <button
                  className={classNames(css.navTab, {
                    [css.navTabActive]: this.state.listingTypeSearch === null,
                    [css.heroTextHidden]: this.state.isSearchMapOpenOnMobile,
                  })}
                  onClick={() => this.setListingTypeFilter(null)}
                  data-elbaction="click: click allFilter"
                >
                  <FormattedMessage id="MainPanelHeader.listingTypesFilter.All" />
                </button>
                <button
                  className={classNames(css.navTab, {
                    [css.navTabActive]: this.state.listingTypeSearch === 'impact-experience',
                    [css.heroTextHidden]: this.state.isSearchMapOpenOnMobile,
                  })}
                  onClick={() => this.setListingTypeFilter('impact-experience')}
                  data-elbaction="click: click expFilter"
                >
                  <FormattedMessage id="MainPanelHeader.listingTypesFilter.Experiences" />
                </button>
                <button
                  className={classNames(css.navTab, {
                    [css.navTabActive]: this.state.listingTypeSearch === 'impact-accommodation',
                    [css.heroTextHidden]: this.state.isSearchMapOpenOnMobile,
                  })}
                  onClick={() => this.setListingTypeFilter('impact-accommodation')}
                  data-elbaction="click: click accmFilter"
                >
                  <FormattedMessage id="MainPanelHeader.listingTypesFilter.Stays2" />
                </button>
              </div>

              <div className={css.controlsRow}>
                <div className={css.navigationTabsDesktop}>
                  <button
                    className={classNames(css.navTab, {
                      [css.navTabActive]: this.state.listingTypeSearch === null,
                    })}
                    onClick={() => this.setListingTypeFilter(null)}
                    data-elbaction="click: click allFilter"
                  >
                    <FormattedMessage id="MainPanelHeader.listingTypesFilter.All" />
                  </button>
                  <button
                    className={classNames(css.navTab, {
                      [css.navTabActive]: this.state.listingTypeSearch === 'impact-experience',
                    })}
                    onClick={() => this.setListingTypeFilter('impact-experience')}
                    data-elbaction="click: click expFilter"
                  >
                    <FormattedMessage id="MainPanelHeader.listingTypesFilter.Experiences" />
                  </button>
                  <button
                    className={classNames(css.navTab, {
                      [css.navTabActive]: this.state.listingTypeSearch === 'impact-accommodation',
                    })}
                    onClick={() => this.setListingTypeFilter('impact-accommodation')}
                    data-elbaction="click: click accmFilter"
                  >
                    <FormattedMessage id="MainPanelHeader.listingTypesFilter.Stays2" />
                  </button>
                </div>
                {propsForSecondaryFiltersToggle.toggleSecondaryFiltersOpen ? (
                  <button
                    type="button"
                    className={classNames(css.controlsFilterButton, {
                      [css.controlsFilterButtonActive]:
                        isSecondaryFiltersOpen || selectedSecondaryFiltersCount > 0,
                    })}
                    onClick={() =>
                      propsForSecondaryFiltersToggle.toggleSecondaryFiltersOpen(!isSecondaryFiltersOpen)
                    }
                    data-elb="searchp"
                    data-elbaction="click: click moreFilter"
                  >
                    <svg
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      aria-hidden="true"
                    >
                      <path d="M3 6h18M6 12h12M10 18h4" />
                    </svg>
                    <FormattedMessage id="MainPanelHeader.filtersButton" defaultMessage="Filter" />
                    {selectedSecondaryFiltersCount > 0 ? (
                      <span className={css.controlsFilterCount}>{selectedSecondaryFiltersCount}</span>
                    ) : null}
                  </button>
                ) : null}
                <div className={css.sortControl}>
                  <span className={css.sortLabel}>
                    <FormattedMessage
                      id="SearchPageWithMap.sortLabel"
                      defaultMessage="Sortieren"
                    />
                  </span>
                  {sortBy('desktop')}
                </div>
              </div>
            </div>

            {/* Secondary Filter dropdown: attached to the sticky bar, it drops down
                directly under the Filter button and overlays the results (absolute)
                instead of a full-width in-flow card that pushes them down. */}
            {isSecondaryFiltersOpen ? (
              <div className={css.filterDropdown}>
                <div className={classNames(css.searchFiltersPanel)}>
                  <SearchFiltersSecondary
                    urlQueryParams={validQueryParams}
                    listingsAreLoaded={listingsAreLoaded}
                    applyFilters={this.applyFilters}
                    cancelFilters={this.cancelFilters}
                    resetAll={this.resetAll}
                    onClosePanel={() => this.setState({ isSecondaryFiltersOpen: false })}
                  >
                    {customSecondaryFilters.map(config => {
                      return (
                        <FilterComponent
                          key={`SearchFiltersSecondary.${config.key}`}
                          idPrefix="SearchFiltersSecondary"
                          config={config}
                          marketplaceCurrency={marketplaceCurrency}
                          urlQueryParams={validQueryParams}
                          initialValues={initialValues(this.props, this.state.currentQueryParams)}
                          getHandleChangedValueFn={this.getHandleChangedValueFn}
                          intl={intl}
                          showAsPopup={false}
                        />
                      );
                    })}
                  </SearchFiltersSecondary>
                </div>
              </div>
            ) : null}
          </div>

          {/* Full-width impact banner (data-driven) below the sticky filter bar. */}
          <div className={css.fullWidthBar}>
            <ImpactBanner listings={listings} intl={intl} resultCount={totalItems} />
          </div>
          <div
            className={css.container}
            data-elbglobals="pagetype:destinations"
            data-elbcontext="booking:discover"
          >
          {/* Results Section - shown for both hero and non-hero views */}
          <div
            className={classNames(css.searchResultContainer, {
              [css.searchResultWithHero]:
                validQueryParams?.address &&
                validQueryParams?.address !== 'undefined' &&
                validQueryParams?.address !== 'null' &&
                validQueryParams?.address.trim() !== '',
            })}
            {...elbsearchLocationData}
          >
            <div className={css.resultsHeader}>
              <div
                className={css.resultsHeaderTitle}
                data-elb="searchp"
                data-elb-searchp={`name:results-count-${totalItems};`}
                data-elbaction={`visible:vis results-amount-${totalItems};`}
              >
                <FormattedMessage
                  id="MainPanelHeader.foundResults"
                  values={{ count: totalItems }}
                />
              </div>
              <div className={css.resultsHeaderSub}>
                {/* Full dynamic subtitle (desktop). On mobile it runs off the
                    screen, so a static short label is shown instead — see the
                    `resultsHeaderSubFull`/`resultsHeaderSubShort` media rules. */}
                <span className={css.resultsHeaderSubFull}>
                  {buildVerifiedProjectsSubtitle(searchParamsInURL, intl)}
                </span>
                <span className={css.resultsHeaderSubShort}>
                  {intl.formatMessage({ id: 'SearchPageWithMap.verifiedProjects' })}
                </span>
              </div>
            </div>
            <div {...elbDataFilter}>
              <div
                className={classNames(css.listingsForMapVariant, {
                  [css.newSearchInProgress]: !(listingsAreLoadedSucc || searchListingsError),
                })}
                onClick={this.clearFocusAndCloseFilters}
              >
                {searchListingsError ? (
                  <H3 className={css.error}>
                    <FormattedMessage id="SearchPage.searchError" />
                  </H3>
                ) : null}
                {!isValidDatesFilter ? (
                  <H5>
                    <FormattedMessage id="SearchPage.invalidDatesFilter" />
                  </H5>
                ) : null}
                <SearchResultsPanel
                  className={css.searchListingsPanel}
                  listings={listings}
                  pagination={
                    (this.isCountryOnly(location.search) ||
                      this.isContinentOnly(location.search)) &&
                    listingsAreLoaded
                      ? pagination
                      : null
                  }
                  search={parse(location.search)}
                  setActiveListing={onActivateListing}
                  isMapVariant
                  nearbyLabelIndex={nearbyLabelIndex}
                  onLoadMore={this.handleLoadMore}
                />
              </div>
            </div>
          </div>
          <div data-elbaction={`wait(10):isAuthenticated:${this.state.userEmail}`}></div>
          <ModalInMobile
            className={css.mapPanel}
            id="SearchPage.map"
            isModalOpenOnMobile={this.state.isSearchMapOpenOnMobile}
            onClose={() => this.setState({ isSearchMapOpenOnMobile: false })}
            showAsModalMaxWidth={MODAL_BREAKPOINT}
            onManageDisableScrolling={onManageDisableScrolling}
          >
            <div
              className={css.mapWrapper}
              data-testid="searchMapContainer"
              onClick={this.clearFocusAndCloseFilters}
              onMouseEnter={this.clearFocusAndCloseFilters}
            >
              {/* Slice 7: "Karte durchsuchen" status badge (map auto-searches on move) */}
             {/*  {shouldShowSearchMap ? (
                <div className={css.searchAreaBadge} aria-hidden="true">
                  <span className={css.searchAreaBadgeDot} />
                  <FormattedMessage id="SearchPageWithMap.searchThisArea" />
                </div>
              ) : null} */}
              {shouldShowSearchMap ? (
                <SearchMap
                  reusableContainerClassName={css.map}
                  activeListingId={activeListingId}
                  bounds={bounds}
                  center={origin}
                  isSearchMapOpenOnMobile={this.state.isSearchMapOpenOnMobile}
                  location={location}
                  listings={listings || []}
                  onMapMoveEnd={this.onMapMoveEnd}
                  onCloseAsModal={() => {
                    onManageDisableScrolling('SearchPage.map', false);
                  }}
                  messages={intl.messages}
                />
              ) : null}
            </div>
          </ModalInMobile>
          {/*   </div> */}
        </div>
        {/* Slice 7: floating "Karte" button on mobile — opens the map modal
            via the same isSearchMapOpenOnMobile state used by onMapIconClick. */}
        <button
          type="button"
          className={classNames(css.floatingMapButton, {
            [css.floatingMapButtonHidden]: this.state.isSearchMapOpenOnMobile,
          })}
          onClick={() => this.setState({ isSearchMapOpenOnMobile: true })}
          data-elbaction="click: click openMapFloating"
        >
          <svg
            className={css.floatingMapButtonIcon}
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M9 3 3 5v16l6-2 6 2 6-2V3l-6 2-6-2z" />
            <path d="M9 3v16M15 5v16" />
          </svg>
          <FormattedMessage id="SearchFiltersMobile.openMapView" />
        </button>
        </div>
      </Page>
    );
  }
}

SearchPageComponent.defaultProps = {
  listings: [],
  pagination: null,
  searchListingsError: null,
  searchParams: {},
  tab: 'listings',
  activeListingId: null,
  topCategory: null,
};

SearchPageComponent.propTypes = {
  listings: array,
  onActivateListing: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  pagination: propTypes.pagination,
  scrollingDisabled: bool.isRequired,
  searchInProgress: bool.isRequired,
  searchListingsError: propTypes.error,
  searchParams: object,
  tab: oneOf(['filters', 'listings', 'map']).isRequired,

  // from useHistory
  history: shape({
    push: func.isRequired,
  }).isRequired,
  // from useLocation
  location: shape({
    search: string.isRequired,
  }).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,

  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,

  // Most-mentioned category within the selected country ({ key, labelId }), or
  // null. Drives the hero's first trust pill when a country is selected.
  topCategory: shape({
    key: string,
    labelId: string,
  }),
};

const EnhancedSearchPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  // Most-mentioned category within the selected country (the `country` URL param
  // is an ISO code, e.g. "IT"). Drives the hero's first trust pill: null (→ the
  // static "453 Partner" pill) when no country is selected.
  const { country: selectedCountry } = parse(location.search);
  const topCategory = useTopCountryCategory(selectedCountry);

  return (
    <SearchPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      topCategory={topCategory}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const {
    currentPageResultIds,
    pagination,
    searchInProgress,
    searchListingsError,
    searchParams,
    activeListingId,
    nearbyLabelIndex,
  } = state.SearchPage;
  const listings = getListingsById(state, currentPageResultIds);

  let isDevEnv = false;
  if (typeof window !== 'undefined') {
    const hostname = window?.location?.hostname;
    isDevEnv = hostname.includes('localhost');
  }

  let filteredListingsNoTests = [];
  if (!isDevEnv) {
    listings.map(listing => {
      if (
        !(
          listing?.attributes?.publicData?.devListing &&
          listing?.attributes?.publicData?.devListing == true
        )
      ) {
        filteredListingsNoTests.push(listing);
      }
    });
  } else {
    filteredListingsNoTests = listings;
  }

  return {
    listings,
    pagination,
    scrollingDisabled: isScrollingDisabled(state),
    searchInProgress,
    searchListingsError,
    searchParams,
    activeListingId,
    nearbyLabelIndex,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onActivateListing: listingId => dispatch(setActiveListing(listingId)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const SearchPage = compose(connect(mapStateToProps, mapDispatchToProps))(EnhancedSearchPage);

export default SearchPage;
